import "@/App.css";
import React, { useState } from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import OfferCalculator from "@/components/OfferCalculator";
import Login from "@/components/Auth/Login";
import Register from "@/components/Auth/Register";
import Onboarding from "@/components/Auth/Onboarding";
import AdminPanel from "@/components/Auth/AdminPanel";
import ForgotPassword from "@/components/Auth/ForgotPassword";
import ProfileSettings from "@/components/Auth/ProfileSettings";
import SavedSessions from "@/components/SavedSessions";

function AppContent() {
  const { user, loading, isAuthenticated, isAdmin, needsOnboarding, logout, refreshUser } = useAuth();
  const [authView, setAuthView] = useState('login');
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showSavedSessions, setShowSavedSessions] = useState(false);
  const [loadedSession, setLoadedSession] = useState(null);

  const handleLoadSession = (session) => {
    setLoadedSession(session);
    setShowSavedSessions(false);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-3 border-[#0FFFE2] border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - show login/register/forgot password
  if (!isAuthenticated) {
    if (authView === 'register') {
      return <Register onSwitchToLogin={() => setAuthView('login')} />;
    }
    if (authView === 'forgot-password') {
      return <ForgotPassword onBackToLogin={() => setAuthView('login')} />;
    }
    return <Login onSwitchToRegister={() => setAuthView('register')} onForgotPassword={() => setAuthView('forgot-password')} />;
  }

  // Authenticated but needs onboarding
  if (needsOnboarding) {
    return <Onboarding />;
  }

  // Authenticated and onboarded - show calculator with header
  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-black/80 backdrop-blur-md border-b border-[#4A0A66]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Greeting */}
            <div className="text-white">
              <span className="text-gray-400">Hello, </span>
              <span className="font-semibold text-[#0FFFE2]">{user?.first_name}</span>
            </div>

            {/* User Menu */}
            <div className="flex items-center gap-3">
              {/* Saved Sessions Button */}
              <button
                onClick={() => setShowSavedSessions(true)}
                data-testid="saved-sessions-button"
                className="px-3 py-2 text-sm font-medium text-[#0FFFE2] hover:text-[#D0FF0B] transition-colors hidden sm:block"
              >
                My Sessions
              </button>

              {/* Admin Button */}
              {isAdmin && (
                <button
                  onClick={() => setShowAdminPanel(true)}
                  data-testid="admin-panel-button"
                  className="px-4 py-2 text-sm font-medium text-[#D0FF0B] border border-[#D0FF0B]/50 rounded-lg hover:bg-[#D0FF0B]/10 transition-all"
                >
                  Manage Users
                </button>
              )}

              {/* Avatar - clickable for profile */}
              <button
                onClick={() => setShowProfileSettings(true)}
                data-testid="profile-button"
                className="relative group"
              >
                {user?.profile_photo ? (
                  <img 
                    src={user.profile_photo} 
                    alt="Profile" 
                    className="w-9 h-9 rounded-full object-cover border-2 border-transparent group-hover:border-[#0FFFE2] transition-all"
                  />
                ) : (
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 border-transparent group-hover:border-[#0FFFE2] transition-all ${
                    isAdmin
                      ? 'bg-gradient-to-r from-[#D0FF0B] to-[#0FFFE2] text-black'
                      : 'bg-[#4A0A66] text-white'
                  }`}>
                    {user?.first_name?.[0]?.toUpperCase() || '?'}
                  </div>
                )}
              </button>

              {/* Logout */}
              <button
                onClick={logout}
                data-testid="logout-button"
                className="px-3 py-2 text-sm text-gray-400 hover:text-[#DF1283] transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - with padding for header */}
      <main className="pt-16">
        <OfferCalculator 
          loadedSession={loadedSession} 
          onClearLoadedSession={() => setLoadedSession(null)}
        />
      </main>

      {/* Modals */}
      {showAdminPanel && (
        <AdminPanel onClose={() => setShowAdminPanel(false)} />
      )}
      
      {showProfileSettings && (
        <ProfileSettings 
          onClose={() => {
            setShowProfileSettings(false);
            refreshUser();
          }} 
        />
      )}
      
      {showSavedSessions && (
        <SavedSessions 
          onClose={() => setShowSavedSessions(false)}
          onLoadSession={handleLoadSession}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
